import React, { useState, useEffect } from 'react';
import { Calendar, dateFnsLocalizer, Views } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, addDays, isSameDay, endOfMonth, startOfMonth, differenceInWeeks } from 'date-fns';
import zhTW from 'date-fns/locale/zh-TW';
import { ButtonGroup, ToggleButton, Button, Modal, Form } from 'react-bootstrap';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import api from '../../services/api';
import './Schedule.css';

// 設置本地化
const locales = {
  'zh-TW': zhTW,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: (date) => startOfWeek(date, { locale: zhTW }),
  getDay,
  locales,
});

const timeSlots = [
  { value: 'ALL_DAY', label: '全天' },
  { value: 'MORNING', label: '上午' },
  { value: 'AFTERNOON', label: '下午' },
];

const statusColors = {
  OPEN: '#28a745',     // 綠色
  BOOKED: '#dc3545',   // 紅色
  RESERVED: '#ffc107', // 黃色
  EMPTY: '#6c757d',   // 灰色
  MOVING: '#265985',   // 深藍色
};

const statusLabels = {
  OPEN: '開課',
  BOOKED: '已預訂',
  RESERVED: '待確認',
  EMPTY: '保留',
  MOVING: '移動中',
};

function WeekView({ currentDate, events, onEventClick, onSlotSelect, selectedEvents }) {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartDay, setDragStartDay] = useState(null);
  const [selectedDays, setSelectedDays] = useState([]);
  const [touchStartTime, setTouchStartTime] = useState(null);
  const [touchTimer, setTouchTimer] = useState(null);
  
  const LONG_PRESS_DURATION = 100; // 長按閾值（毫秒）

  const startDate = startOfWeek(currentDate, { locale: zhTW });
  const days = Array.from({ length: 7 }, (_, i) => addDays(startDate, i));
  const timeSlots = ['上午', '下午'];

  // 處理拖曳開始
  const handleDragStart = (day) => {
    setIsDragging(true);
    setDragStartDay(day);
    setSelectedDays([day]);
  };

  // 處理拖曳過程中
  const handleDragOver = (day) => {
    if (isDragging && dragStartDay) {
      const startIndex = days.findIndex(d => isSameDay(d, dragStartDay));
      const currentIndex = days.findIndex(d => isSameDay(d, day));
      
      const start = Math.min(startIndex, currentIndex);
      const end = Math.max(startIndex, currentIndex);
      
      const selectedRange = days.slice(start, end + 1);
      setSelectedDays(selectedRange);
    }
  };

  // 處理拖曳結束
  const handleDragEnd = () => {
    if (selectedDays.length > 0) {
      onSlotSelect({
        dates: selectedDays,
        timeSlot: 'ALL_DAY'
      });
    }
    setIsDragging(false);
    setDragStartDay(null);
    setSelectedDays([]);
  };

  // 處理觸控開始
  const handleTouchStart = (day, e) => {
    const startTime = Date.now();
    setTouchStartTime(startTime);
    
    // 設置長按計時器
    const timer = setTimeout(() => {
      e.preventDefault(); // 只在長按時阻止預設行為
      setIsDragging(true);
      setDragStartDay(day);
      setSelectedDays([day]);
    }, LONG_PRESS_DURATION);
    
    setTouchTimer(timer);
  };

  // 處理觸控移動
  const handleTouchMove = (day, e) => {
    // 如果不是長按狀態，允許正常滾動
    if (!isDragging) {
      clearTimeout(touchTimer);
      return;
    }

    // 在控制台顯示拖曳狀態
    console.log('拖曳狀態:', isDragging ? '正在拖曳' : '未拖曳');

    // 防止瀏覽器預設的觸控行為
    e.preventDefault();
    if (isDragging && dragStartDay) {
      const touch = e.touches[0];
      const element = document.elementFromPoint(touch.clientX, touch.clientY);
      const touchedDayIndex = element?.dataset?.dayIndex;
      
      if (touchedDayIndex !== undefined) {
        const startIndex = days.findIndex(d => isSameDay(d, dragStartDay));
        const currentIndex = parseInt(touchedDayIndex);
        
        const start = Math.min(startIndex, currentIndex);
        const end = Math.max(startIndex, currentIndex);
        
        const selectedRange = days.slice(start, end + 1);
        setSelectedDays(selectedRange);
      }
    }
  };

  // 處理觸控結束
  const handleTouchEnd = (e) => {
    clearTimeout(touchTimer);
    
    // 如果不是長按狀態，不處理選擇
    if (!isDragging) {
      return;
    }

    e.preventDefault();
    if (selectedDays.length > 0) {
      onSlotSelect({
        dates: selectedDays,
        timeSlot: 'ALL_DAY'
      });
    }
    
    setIsDragging(false);
    setDragStartDay(null);
    setSelectedDays([]);
    setTouchStartTime(null);
  };

  // 處理觸控取消
  const handleTouchCancel = () => {
    clearTimeout(touchTimer);
    setIsDragging(false);
    setDragStartDay(null);
    setSelectedDays([]);
    setTouchStartTime(null);
  };

  useEffect(() => {
    // 添加事件監聽器，設置 passive: false
    const element = document.querySelector('.week-view');
    if (!element) return;

    const preventScroll = (e) => {
      if (isDragging) {
        e.preventDefault();
      }
    };

    element.addEventListener('touchmove', preventScroll, { passive: false });

    return () => {
      element.removeEventListener('touchmove', preventScroll);
    };
  }, [isDragging]);

  return (
    <div className="week-view">
      {/* 時段標題列 */}
      <div className="time-header">
        <div className="day-label"></div>
        {timeSlots.map((slot) => (
          <div key={slot} className="time-label">{slot}</div>
        ))}
      </div>

      {/* 每天的行 */}
      {days.map((day, dayIndex) => (
        <div key={dayIndex} className="day-row">
          {/* 日期標籤 */}
          <div 
            className={`day-label ${selectedDays.some(d => isSameDay(d, day)) ? 'selected' : ''}`}
            style={{
              display: 'flex', 
              cursor: 'pointer',
              userSelect: 'none',
              WebkitTouchCallout: 'none',
              WebkitUserSelect: 'none',
              KhtmlUserSelect: 'none',
              MozUserSelect: 'none',
              msUserSelect: 'none',
              //touchAction: 'none',
              //color: isDragging ? 'red' : 'black',
              backgroundColor: selectedDays.some(d => isSameDay(d, day)) ? '#e3f2fd' : 'transparent'
            }}
            data-day-index={dayIndex}
            onMouseDown={() => handleDragStart(day)}
            onMouseEnter={() => handleDragOver(day)}
            onMouseUp={handleDragEnd}
            onTouchStart={(e) => handleTouchStart(day, e)}
            onTouchMove={(e) => handleTouchMove(day, e)}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchCancel}
          >
            <div className="day-name">{format(day, 'eee', { locale: zhTW })}</div>
            <div className="day-date">{format(day, 'MM/dd')}</div>
          </div>
          
          {/* 時段格子 */}
          {timeSlots.map((slot, slotIndex) => {
            const dayEvents = events.filter(event => {
              const eventDate = new Date(event.start);
              return (
                isSameDay(eventDate, day) &&
                event.timeSlot === (slotIndex === 0 ? 'MORNING' : 'AFTERNOON')
              );
            });

            return (
              <div
                key={slot}
                className="time-slot"
                onClick={() => onSlotSelect({
                  date: day,
                  timeSlot: slotIndex === 0 ? 'MORNING' : 'AFTERNOON', 
                })}
              >
                {dayEvents.map((event, eventIndex) => (
                  <div
                    key={eventIndex}
                    // 將 selectedEvents 作為 props 傳入 WeekView 組件
                    className={`event ${selectedEvents.some(e => e.id === event.id) ? 'selected' : ''}`}
                    style={{ 
                      backgroundColor: statusColors[event.status],
                      cursor: event.status === 'BOOKED' || event.status === 'RESERVED' ? 'not-allowed' : 'pointer'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (event.status !== 'BOOKED' && event.status !== 'RESERVED') {
                        onEventClick(event, e.ctrlKey || e.metaKey);
                      }
                    }}
                  >
                    {event.location}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function Schedule() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewType, setViewType] = useState('month');
  const [events, setEvents] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedDates, setSelectedDates] = useState([]);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState('ALL_DAY');
  const [status, setStatus] = useState('OPEN');
  const [location, setLocation] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);

  useEffect(() => {
    fetchSchedules();
  }, []);

  const fetchSchedules = async () => {
    try {
      console.log('Fetching schedules...');
      const response = await api.get('/schedules/');
      console.log('Schedules response:', response.data);
      // 根據日期和時段排序
      response.data.sort((a, b) => {
        // 先比較日期
        const dateComparison = new Date(a.date) - new Date(b.date);
        if (dateComparison !== 0) {
          return dateComparison;
        }
        // 日期相同時,比較時段 (MORNING 排在 AFTERNOON 前面)
        return a.time_slot === 'MORNING' ? -1 : 1;
      });

      const formattedEvents = response.data.map(schedule => ({
        id: schedule.id,
        title: schedule.location,
        start: new Date(schedule.date),
        end: new Date(schedule.date),
        status: schedule.status,
        timeSlot: schedule.time_slot,
        location: schedule.location,
        allDay: true
      }));

      console.log('Formatted events:', formattedEvents);
      setEvents(formattedEvents);
    } catch (err) {
      console.error('Error fetching schedules:', err.response || err);
    }
  };
/*
  useEffect(() => {
    const testEvents = [
      {
        id: 1,
        title: '上午 - 開課',
        start: new Date(),
        end: new Date(),
        status: 'OPEN',
        timeSlot: 'MORNING',
        allDay: true
      },
      {
        id: 2,
        title: '下午 - 已預訂',
        start: new Date(),
        end: new Date(),
        status: 'BOOKED',
        timeSlot: 'AFTERNOON',
        allDay: true
      }
    ];
    console.log('Setting test events:', testEvents);
    setEvents(testEvents);
  }, []);
*/
  const handleStatusChange = async () => {
    if (!location) {
      setError('請輸入地點');
      return;
    }

    try {
      await api.patch(`/schedules/${selectedEvent.id}/change_schedule/`, {
        status: newStatus,
        location: location
      });
      await fetchSchedules();
      setShowStatusModal(false);
    } catch (err) {
      setError('更新狀態失敗');
    }
  };

  // 週曆選擇時段
  const handleSlotSelectWeek = (selection) => {
    console.log('handleSlotSelectWeek:', selection);
    setSelectedDates(selection.dates || [selection.date]);
    setSelectedTimeSlot(selection.timeSlot);
    
    // 檢查選擇的時段是否已有事件
    const selectedEvents = events.filter(event => {
      const selectedDate = selection.dates ? selection.dates[0] : selection.date;
      return isSameDay(new Date(event.start), selectedDate) && 
             event.timeSlot === selection.timeSlot;
    });
    
    // 根據是否存在事件設置狀態
    if (selectedEvents.length > 0) {
      handleEventSelect(selectedEvents[0], false);
    } else {
      setStatus('OPEN');
      setError('');
      setShowModal(true);
    }
  };

  // 月曆選擇時段
  const handleSlotSelect = ({ start, end, slots }) => {
    console.log('handleSlotSelect:', { start, end, slots });
    const dates = slots ? slots.map(slot => new Date(slot)) : [new Date(start)];
    console.log('Selected dates:', dates);
    setSelectedDates(dates);
    setSelectedTimeSlot('ALL_DAY');
    setStatus('OPEN');
    setError('');
    setShowModal(true);
  };

  // 提交新增行程
  const handleSubmit = async () => {
    if (!selectedTimeSlot) {
      setError('請選擇時段');
      return;
    }

    if (!location) {
      setError('請輸入地點');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      let processedSlots = [];
      if (selectedTimeSlot === 'ALL_DAY') {
        processedSlots = ['MORNING', 'AFTERNOON'];
      } else {
        processedSlots = [selectedTimeSlot];
      }

      const dates = selectedDates.map(date => 
        format(new Date(date.getFullYear(), date.getMonth(), date.getDate()), 'yyyy-MM-dd')
      );

      const response = await api.post('/schedules/bulk_create/', {
        dates,
        time_slots: processedSlots,
        status,
        location
      });

      await fetchSchedules();
      setShowModal(false);
      setSelectedDates([]);
      setSelectedTimeSlot('');
    } catch (err) {
      console.log('創建行程錯誤:', err.response?.data);
      
      // 處理不同類型的錯誤
      if (err.response?.data) {
        // 如果是陣列，顯示所有錯誤
        if (Array.isArray(err.response.data)) {
          setError(err.response.data.join('\n'));
        } 
        // 如果是物件，顯示每個欄位的錯誤
        else if (typeof err.response.data === 'object') { 
          const errorMessages = Object.entries(err.response.data)
            .map(([field, message]) => {
              return message;
            });
          setError(errorMessages.join('\n'));
        }
      } else if (err.response?.status === 400) {
        setError('無效的請求：請檢查所有欄位是否正確');
      } else if (err.response?.status === 401) {
        setError('未授權：請重新登入');
      } else if (err.response?.status === 403) {
        setError('權限不足：只有教練可以創建行程');
      } else if (err.response?.status === 409) {
        setError('時段衝突：選擇的時段已經有行程');
      } else {
        setError('創建行程失敗，請稍後再試');
      }
    } finally {
      setLoading(false);
    }
  };

  const viewTypes = [
    { name: '週曆', value: 'week' },
    { name: '月曆', value: 'month' },
  ];

  // 處理視圖切換
  const handleViewChange = (newView) => {
    setViewType(newView);
    // 可以在這裡添加其他視圖切換相關的邏輯
    console.log('View changed to:', newView);
  };

  // 處理上一週/月
  const handlePrevious = () => {
    setCurrentDate(prev => {
      if (viewType === 'week') {
        return addDays(prev, -7);
      } else {
        // 月視圖
        const newDate = new Date(prev);
        newDate.setMonth(prev.getMonth() - 1);
        return newDate;
      }
    });
  };

  // 處理下一週/月
  const handleNext = () => {
    setCurrentDate(prev => {
      if (viewType === 'week') {
        return addDays(prev, 7);
      } else {
        // 月視圖
        const newDate = new Date(prev);
        newDate.setMonth(prev.getMonth() + 1);
        return newDate;
      }
    });
  };
/*
  // 處理今天按鈕
  const handleToday = () => {
    setCurrentDate(new Date());
  };
*/
  // 月曆視圖的事件樣式
  const eventStyleGetter = (event) => {
    return {
      style: {
        backgroundColor: statusColors[event.status],
        borderRadius: '3px',
        opacity: 0.8,
        color: 'white',
        border: 'none',
        display: 'block',
        padding: '2px 5px',
        cursor: event.status === 'BOOKED' || event.status === 'RESERVED' ? 'default' : 'pointer',
      }
    };
  };

  // 處理多選
  const handleEventSelect = (event, isCtrlPressed) => {
    if (event.status === 'BOOKED') {
      console.log('已預訂的課程無法修改');
      return;
    }
    else if (event.status === 'RESERVED') {
      console.log('待確認的課程無法修改');
      return;
    }

    if (isCtrlPressed === true) {
      // Ctrl + 點擊：切換選擇狀態
      setSelectedEvents(prev => {
        const isSelected = prev.some(e => e.id === event.id);
        
        if (isSelected) {
          // 如果已經選中，則移除該事件
          return prev.filter(e => e.id !== event.id);
        } else {
          // 添加新事件並按日期和時段排序
          const newEvents = [...prev, event].sort((a, b) => {
            // 首先比較日期
            const dateA = new Date(a.start);
            const dateB = new Date(b.start);
            
            if (dateA.getTime() !== dateB.getTime()) {
              return dateA - dateB;
            }
            
            // 如果日期相同，則按時段排序（上午優先）
            const timeSlotOrder = {
              'MORNING': 0,
              'AFTERNOON': 1
            };
            
            return timeSlotOrder[a.timeSlot] - timeSlotOrder[b.timeSlot];
          });
          
          return newEvents;
        }
      });
      console.log('selectedEvents:', selectedEvents);
      console.log('selectedEvent Length:', selectedEvents.length);
    } else {
      // 單擊：顯示單一事件的編輯模態框
      setError('');
      setSelectedEvent(event);
      setLocation(event.location);
      setNewStatus(event.status);
      setShowStatusModal(true);
    }
  };

  // 批量更新狀態
  const handleBulkUpdate = async () => {
    if (!location) {
      setError('請輸入地點');
      return;
    }

    try {
      setLoading(true);
      await api.patch('/schedules/bulk_update_schedule/', {
        schedule_ids: selectedEvents.map(event => event.id),
        status: newStatus,
        location: location
      });
      
      await fetchSchedules();
      setShowBulkUpdateModal(false);
      setSelectedEvents([]);
      setLocation('');
      setNewStatus('');
    } catch (err) {
      setError('更新失敗');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // 計算日曆需要顯示的確切週數
    const firstDayOfMonth = startOfMonth(currentDate);
    const firstDayWeekday = getDay(firstDayOfMonth);
    const daysBeforeMonth = firstDayWeekday === 0 ? 6 : firstDayWeekday - 1; // 調整為週一開始
    const firstCalendarDate = addDays(firstDayOfMonth, -daysBeforeMonth);
    
    const lastDayOfMonth = endOfMonth(currentDate);
    const lastDayWeekday = getDay(lastDayOfMonth);
    const daysAfterMonth = lastDayWeekday === 0 ? 0 : 7 - lastDayWeekday;
    const lastCalendarDate = addDays(lastDayOfMonth, daysAfterMonth);

    const weeksInMonth = Math.ceil(differenceInWeeks(lastCalendarDate, firstCalendarDate, { roundingMethod: 'ceil' }));
    
    console.log('firstCalendarDate:', firstCalendarDate);
    console.log('lastCalendarDate:', lastCalendarDate);
    console.log('weeksInMonth:', weeksInMonth);
    
    const container = document.querySelector('.calendar-container');
    if (container) {
      container.dataset.weeks = weeksInMonth;  // 設置 data-weeks 屬性
      container.style.setProperty('--number-of-weeks', weeksInMonth);
    }
  }, [currentDate]);

  return (
    <div className="schedule-container">
      <div className="schedule-header">
        <div className="schedule-controls">
          {/* 導航按鈕和日期顯示 */}
          <div className="navigation-controls">
            <Button variant="outline-secondary" size="sm" onClick={handlePrevious}>
              &lt; {viewType === 'week' ? '上週' : '上月'}
            </Button>
            {/*
            <Button variant="outline-secondary" size="sm" onClick={handleToday}>
              今天
            </Button>
            */}
            <span className="current-week">
              {format(currentDate, 'yyyy年MM月')}
            </span>
            <Button variant="outline-secondary" size="sm" onClick={handleNext}>
              {viewType === 'week' ? '下週' : '下月'} &gt;
            </Button>
          </div>

          {/* 視圖切換按鈕組移到最右邊 */}
          <ButtonGroup className="ms-3">
            {viewTypes.map((type) => (
              <ToggleButton
                key={type.value}
                id={`view-type-${type.value}`}
                type="radio"
                variant="outline-primary"
                name="view-type"
                value={type.value}
                checked={viewType === type.value}
                onChange={(e) => handleViewChange(e.currentTarget.value)}
              >
                {type.name}
              </ToggleButton>
            ))}
          </ButtonGroup>
        </div>
      </div>

      <div className="schedule-legend">
        {Object.entries(statusLabels).map(([key, label]) => (
          <div key={key} className="legend-item">
            <span 
              className="legend-color" 
              style={{ backgroundColor: statusColors[key] }}
            ></span>
            <span>{label}</span>
          </div>
        ))}
      </div>

      {viewType === 'week' ? (
        <WeekView
          currentDate={currentDate}
          events={events}
          onEventClick={(handleEventSelect)}
          onSlotSelect={handleSlotSelectWeek}
          selectedEvents={selectedEvents}
        />
      ) : (
        <div className="calendar-container">
          <div className="calendar-content">
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              selectable
              onSelectSlot={handleSlotSelect}
              onSelectEvent={handleEventSelect}
              eventPropGetter={eventStyleGetter}
              defaultView={Views.MONTH}
              views={[Views.MONTH]}
              toolbar={false}
              date={currentDate}
              onNavigate={(date) => setCurrentDate(date)}
              messages={{
                showMore: total => `還有 ${total} 個...`
              }}
              formats={{
                monthHeaderFormat: date => format(date, 'yyyy年MM月'),
                dayHeaderFormat: date => format(date, 'MM/dd'),
                dayRangeHeaderFormat: ({ start, end }) => 
                  `${format(start, 'yyyy年MM月dd日')} - ${format(end, 'MM月dd日')}`,
                dayFormat: date => format(date, 'd')
              }}
              components={{
                event: ({ event }) => (
                  <div
                    className={`event ${selectedEvents.some(e => e.id === event.id) ? 'selected' : ''}`}
                    style={{
                      backgroundColor: statusColors[event.status],
                      color: 'white',
                      padding: '2px 5px',
                      borderRadius: '3px',
                      fontSize: '0.85em',
                      cursor: event.status === 'BOOKED' || event.status === 'RESERVED' ? 'default' : 'pointer',
                      pointerEvents: event.status === 'BOOKED' || event.status === 'RESERVED' ? 'none' : 'auto'
                    }}
                    
                    onClick={(e) => {
                      e.stopPropagation();
                      if (event.status !== 'BOOKED' && event.status !== 'RESERVED') {
                        handleEventSelect(event, e.ctrlKey || e.metaKey);
                      }
                    }}
                    
                  >
                    {event.title}
                  </div>
                ),
                header: ({ date }) => {
                  const weekdays = ['週日', '週一', '週二', '週三', '週四', '週五', '週六'];
                  return <span>{weekdays[date.getDay()]}</span>;
                }
              }}
            />
          </div>
        </div>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>新增課程</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <div className="alert alert-danger" style={{ whiteSpace: 'pre-line' }}>
              {error}
            </div>
          )}
          
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>選擇時段</Form.Label>
              <Form.Select
                value={selectedTimeSlot}
                onChange={(e) => setSelectedTimeSlot(e.target.value)}
                disabled={loading}
              >
                {timeSlots.map(slot => (
                  <option key={slot.value} value={slot.value}>
                    {slot.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>狀態</Form.Label>
              <Form.Select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                disabled={loading}
              >
                <option value="OPEN">開課</option>
                <option value="EMPTY">保留</option>
                <option value="MOVING">移動中</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>地點</Form.Label>
              <Form.Control 
                type="text" 
                value={location} 
                onChange={(e) => setLocation(e.target.value)} 
              />
            </Form.Group>

            <div className="selected-dates mt-3">
              <h6>選擇的日期：</h6>
              {selectedDates.length === 0 ? (
                <p className="text-muted">尚未選擇日期</p>
              ) : selectedDates.length === 1 ? (
                <p>{format(selectedDates[0], 'yyyy年MM月dd日')}</p>
              ) : (
                <p>
                  {format(selectedDates[0], 'yyyy年MM月dd日')} ~ {format(selectedDates[selectedDates.length - 1], 'yyyy年MM月dd日')}
                </p>
              )}
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            取消
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSubmit}
            disabled={loading || !selectedTimeSlot || selectedDates.length === 0}
          >
            {loading ? '處理中...' : '確認'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showStatusModal} onHide={() => {
        setError('');
        setShowStatusModal(false);
      }}>
        <Modal.Header closeButton>
          <Modal.Title>更改課程狀態</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <div className="alert alert-danger" style={{ whiteSpace: 'pre-line' }}>
              {error}
            </div>
          )}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>目前狀態：{selectedEvent && statusLabels[selectedEvent.status]}</Form.Label>
              <Form.Select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
              >
                <option value="OPEN">開課</option>
                <option value="EMPTY">保留</option>
                <option value="MOVING">移動中</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>地點</Form.Label>
              <Form.Control
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStatusModal(false)}>
            取消
          </Button>
          <Button 
            variant="primary" 
            onClick={handleStatusChange}
            disabled={loading || !newStatus || !location}
          >
            {loading ? '處理中...' : '確認'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 添加批量更新按鈕 */}
      {selectedEvents.length > 0 && (
        <Button
          variant="primary"
          className="mb-3 bulk-update-button"
          onClick={() => {
            setError('');
            setShowBulkUpdateModal(true);
          }}
        >
          批量更新 ({selectedEvents.length} 個行程)
        </Button>
      )}

      {/* 添加批量更新模態框 */}
      <Modal show={showBulkUpdateModal} onHide={() => setShowBulkUpdateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>批量更新課程</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <div className="alert alert-danger" style={{ whiteSpace: 'pre-line' }}>
              {error}
            </div>
          )}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>新狀態</Form.Label>
              <Form.Select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
              >
                <option value="">請選擇狀態</option>
                <option value="OPEN">開課</option>
                <option value="EMPTY">保留</option>
                <option value="MOVING">移動中</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>地點</Form.Label>
              <Form.Control
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              />
            </Form.Group>
            <div>
              <h6>選擇的行程：</h6>
              <ul>
                {selectedEvents.map(event => (
                  <li key={event.id}>
                    {format(new Date(event.start), 'yyyy/MM/dd')} - 
                    {event.timeSlot === 'MORNING' ? '上午' : '下午'}
                  </li>
                ))}
              </ul>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" 
            onClick={() => {
              setShowBulkUpdateModal(false);
              setSelectedEvents([]);
            }}
          >
            取消
          </Button>
          <Button 
            variant="primary" 
            onClick={handleBulkUpdate}
            disabled={loading || !newStatus || !location}
          >
            {loading ? '處理中...' : '確認'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default Schedule; 